import React, { useId, useMemo } from 'react';
import { useAppSelector } from '../../hooks/redux';
import type { AmhsLiveReportPayload } from '../../types/amhsStream';
import type { AmhsFinalReport, AmhsKpiBlock, AmhsRunReports } from '../../types/simulationPlayback';

type Props = {
  open: boolean;
  onClose: () => void;
};

/** Temporarily hidden: the Throughput cards in the report modal. */
const SHOW_THROUGHPUT_CARDS = false;

/**
 * Hover explanations for each KPI card. Each entry states the formula the UI or
 * API uses and what the terms mean, so a reader can reproduce the number from
 * the raw report. Keep in step with `live_report.py` and the helpers above.
 */
const METRIC_HELP = {
  cycleTime:
    'Cycle time = makespan ÷ 3600.\n' +
    'Makespan is the simulation clock, in seconds, when the last transport request finished. ' +
    'Dividing by 3600 expresses the whole run as hours per lot cycle. Lower is better.',
  throughput:
    'Throughput = completed requests ÷ makespan × 86,400.\n' +
    'The number of transport requests finished inside the run, scaled to a 24-hour day. Higher is better.',
  ohtUtilization:
    'OHT utilization = total busy seconds ÷ (makespan × vehicle count).\n' +
    'Busy seconds are added up over every OHT for each scheduled leg it is on: empty travel to a pickup (deadhead), ' +
    'loaded delivery, and waiting at a process port. Idle or parked time is not busy. ' +
    'Vehicle count includes OHTs that never moved, so unused vehicles pull the figure down. Higher is better.',
  makespan:
    'Makespan = simulation seconds elapsed when the last job finished.\n' +
    'For a live stream this is the current server simulation time, so it grows while the run is in progress. ' +
    'It is the denominator used by cycle time, throughput and utilization.',
  completed:
    'Completed = count of distinct transport requests whose drop finished within the horizon.\n' +
    'Each request ID is counted once, however many legs it took.',
  avgWait:
    'Avg wait = sum of each completed request’s wait seconds ÷ completed requests.\n' +
    'Wait is the time a request stood blocked before pickup: waiting for a free vehicle, a locked port, or junction clearance.',
  avgDelivery:
    'Avg delivery = sum of (planned end − planned start) ÷ completed requests.\n' +
    'For each finished request this is the time from its planned pickup to its planned drop, including travel and dock holds.',
  vehicles:
    'Vehicles = number of distinct OHT IDs in the run roster.\n' +
    'Includes vehicles that never received a request. This is the vehicle count in the utilization formula.',
  improvement:
    '\nBefore = the same run simulated without optimization (FIFO assignment). After = with optimization. ' +
    'Improvement % = (before − after) ÷ before for lower-is-better metrics, and (after − before) ÷ before for higher-is-better ones.',
} as const;

/**
 * Info marker plus hover / focus tooltip explaining how a card’s figure is
 * computed. The card root must carry `group relative` and the matching
 * `aria-describedby` so the panel is reachable from the keyboard too.
 */
function MetricHelp({ id, text }: { id: string; text: string }) {
  const paragraphs = text.split('\n').filter((line) => line.trim().length > 0);
  return (
    <>
      <span
        aria-hidden="true"
        className="ml-1 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-current text-[8px] font-bold leading-none opacity-60 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        i
      </span>
      <div
        id={id}
        role="tooltip"
        className="pointer-events-none invisible absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-[11px] font-normal normal-case leading-relaxed tracking-normal text-slate-700 opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      >
        {paragraphs.map((line, index) => (
          <p key={index} className={index === 0 ? 'font-medium text-slate-900' : 'mt-1'}>
            {line}
          </p>
        ))}
      </div>
    </>
  );
}

function fmt(n: unknown, digits = 1): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: Number.isInteger(n) ? 0 : Math.min(digits, 1),
  });
}

function util(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${fmt(n <= 1 ? n * 100 : n, 1)}%`;
}

/** Before/after improvement column: (+) when better, (−) when worse. */
function improveDeltaLabel(n: unknown): { text: string; improved: boolean | null } {
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    return { text: '—', improved: null };
  }
  if (Math.abs(n) < 0.05) {
    return { text: '0.0%', improved: null };
  }
  if (n > 0) {
    return { text: `(+) ${fmt(n, 1)}%`, improved: true };
  }
  return { text: `(−) ${fmt(Math.abs(n), 1)}%`, improved: false };
}

/** Lot cycle time (hr) = system makespan / 3600. */
function cycleHours(kpis?: AmhsKpiBlock | null): number | null {
  const m = kpis?.makespanS;
  if (typeof m !== 'number' || !Number.isFinite(m) || m <= 0) return null;
  return m / 3600;
}

/** Throughput (lots/day) = completedRequests / makespanS × 86400. */
function lotsPerDay(kpis?: AmhsKpiBlock | null): number | null {
  const m = kpis?.makespanS;
  const c = kpis?.completedRequests;
  if (typeof m !== 'number' || typeof c !== 'number' || !Number.isFinite(m) || m <= 0) {
    return null;
  }
  return (c / m) * 86400;
}

function improvePct(
  before: number | null,
  after: number | null,
  higherIsBetter: boolean
): number | null {
  if (before == null || after == null || !Number.isFinite(before) || before === 0) return null;
  return higherIsBetter
    ? ((after - before) / Math.abs(before)) * 100
    : ((before - after) / Math.abs(before)) * 100;
}

/**
 * Playback / Optimized-run KPIs — finalSimulation (CA > route > dispatch).
 */
function getPlaybackAfterKpis(reports: AmhsRunReports): AmhsKpiBlock | undefined {
  return (
    reports.finalSimulationKpis ?? reports.report?.simulation?.kpis ?? getCompareAfterKpis(reports)
  );
}

/**
 * Compare “after” — SimPy withOptimization (same engine as without).
 * Use for Improvement focus so gains are not mixed with CA DES noise.
 */
function getCompareAfterKpis(reports: AmhsRunReports): AmhsKpiBlock | undefined {
  return (
    reports.summary?.simulationWithKpis ??
    reports.simulationCompareReport?.compare?.withOptimization
  );
}

/** Baseline KPIs from the without-optimization compare path. */
function getBeforeKpis(reports: AmhsRunReports): AmhsKpiBlock | undefined {
  return (
    reports.summary?.simulationWithoutKpis ??
    reports.simulationCompareReport?.compare?.withoutOptimization
  );
}

/**
 * Classic OHT utilization from KPIs: busy ÷ (makespan × vehicles).
 * Matches docs Before vs After Summary (e.g. 68.7% → 69.0%).
 * Do not substitute productive/delivery util here — that inflates the %.
 */
function ohtUtilization(kpis?: AmhsKpiBlock | null): number | null {
  if (!kpis) return null;
  const u = kpis.ohtUtilization;
  if (typeof u !== 'number' || !Number.isFinite(u)) return null;
  // API stores fraction 0–1; tolerate accidental percent values.
  return u > 1 && u <= 100 ? u / 100 : u;
}

function Section({
  title,
  description,
  children,
}: {
  /** Omit to render the section body with no heading. */
  title?: string;
  description?: string;
  children: React.ReactNode;
}) {
  const hasHeader = Boolean(title) || Boolean(description);
  return (
    <section className="space-y-2">
      {hasHeader ? (
        <div>
          {title ? (
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {title}
            </h3>
          ) : null}
          {description ? (
            <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{description}</p>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

function FocusMetricCard({
  title,
  subtitle,
  before,
  after,
  improvementPct,
  unit,
  help,
}: {
  title: string;
  subtitle: string;
  before: string;
  after: string;
  improvementPct: number | null;
  unit?: string;
  help?: string;
}) {
  const deltaLabel = improveDeltaLabel(improvementPct);
  const helpId = useId();

  return (
    <div
      tabIndex={help ? 0 : undefined}
      aria-describedby={help ? helpId : undefined}
      className="group relative rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-sky-50 px-4 py-4 shadow-sm ring-1 ring-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
    >
      <div className="flex items-center text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
        {title}
        {help ? <MetricHelp id={helpId} text={help} /> : null}
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Before
          </div>
          <div className="mt-0.5 text-xl font-bold tabular-nums text-slate-500">
            {before}
            {unit ? (
              <span className="ml-1 text-xs font-semibold text-slate-400">{unit}</span>
            ) : null}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
            After
          </div>
          <div className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
            {after}
            {unit ? (
              <span className="ml-1 text-xs font-semibold text-slate-500">{unit}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div
        className={`mt-3 inline-flex rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${
          deltaLabel.improved === true
            ? 'bg-emerald-600 text-white'
            : deltaLabel.improved === false
              ? 'bg-amber-100 text-amber-800'
              : 'bg-slate-100 text-slate-600'
        }`}
      >
        Improvement {deltaLabel.text}
      </div>
    </div>
  );
}

function FocusImprovementPanel({ reports }: { reports: AmhsRunReports }) {
  const withK = getCompareAfterKpis(reports);
  const withoutK = getBeforeKpis(reports);

  const cycleBefore = cycleHours(withoutK);
  const cycleAfter = cycleHours(withK);
  const thrBefore = lotsPerDay(withoutK);
  const thrAfter = lotsPerDay(withK);
  const utilBefore = ohtUtilization(withoutK);
  const utilAfter = ohtUtilization(withK);

  // Always derive % from the same before/after numbers shown on the cards.
  const cycleImp = improvePct(cycleBefore, cycleAfter, false);
  const thrImp = improvePct(thrBefore, thrAfter, true);
  const utilImp = improvePct(utilBefore, utilAfter, true);

  if (!withK && !withoutK) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <FocusMetricCard
        title="Cycle time"
        subtitle="Lot cycle = makespan ÷ 3600"
        before={cycleBefore != null ? fmt(cycleBefore, 2) : '—'}
        after={cycleAfter != null ? fmt(cycleAfter, 2) : '—'}
        improvementPct={cycleImp}
        unit="hr"
        help={METRIC_HELP.cycleTime + METRIC_HELP.improvement}
      />
      {SHOW_THROUGHPUT_CARDS && (
        <FocusMetricCard
          title="Throughput"
          subtitle="Completed lots ÷ makespan × 24h"
          before={thrBefore != null ? fmt(thrBefore, 1) : '—'}
          after={thrAfter != null ? fmt(thrAfter, 1) : '—'}
          improvementPct={thrImp}
          unit="lots/day"
          help={METRIC_HELP.throughput + METRIC_HELP.improvement}
        />
      )}
      <FocusMetricCard
        title="OHT utilization"
        subtitle="Busy time ÷ (makespan × vehicles)"
        before={util(utilBefore)}
        after={util(utilAfter)}
        improvementPct={utilImp}
        help={METRIC_HELP.ohtUtilization + METRIC_HELP.improvement}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  highlight,
  help,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
  help?: string;
}) {
  const helpId = useId();
  return (
    <div
      tabIndex={help ? 0 : undefined}
      aria-describedby={help ? helpId : undefined}
      className={`group relative rounded-xl border px-3 py-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
        highlight
          ? 'border-emerald-300 bg-emerald-50 ring-1 ring-emerald-100'
          : 'border-slate-200 bg-slate-50'
      }`}
    >
      <div
        className={`flex items-center text-[10px] font-semibold uppercase tracking-wider ${
          highlight ? 'text-emerald-800' : 'text-slate-500'
        }`}
      >
        {label}
        {help ? <MetricHelp id={helpId} text={help} /> : null}
      </div>
      <div
        className={`mt-0.5 text-lg font-semibold tabular-nums ${
          highlight ? 'text-emerald-950' : 'text-slate-900'
        }`}
      >
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-[10px] text-slate-400">{hint}</div> : null}
    </div>
  );
}

function reportsFromLive(live: AmhsLiveReportPayload | null): AmhsRunReports | null {
  if (!live) return null;
  return {
    report: live.report as AmhsFinalReport | undefined,
    finalSimulationKpis: live.finalSimulationKpis as AmhsKpiBlock | undefined,
  };
}

const AmhsReportsModal: React.FC<Props> = ({ open, onClose }) => {
  const batchReports = useAppSelector((s) => s.simulation.amhsReports);
  const livePayload = useAppSelector(
    (s) => s.simulation.live.liveReport ?? s.simulation.lastLiveReport
  );
  const reports = useMemo(
    () => batchReports ?? reportsFromLive(livePayload),
    [batchReports, livePayload]
  );

  const simKpis = reports ? getPlaybackAfterKpis(reports) : undefined;
  const byOht = reports?.report?.optimization?.byOht ?? [];

  const afterCycle = cycleHours(simKpis);
  const afterThr = lotsPerDay(simKpis);
  const afterOhtUtil = ohtUtilization(simKpis);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="AMHS simulation reports"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {reports?.report?.title ?? 'AMHS simulation reports'}
            </h2>
            {/* <p className="mt-0.5 text-xs text-slate-500">
              Improvement: SimPy with vs without · Optimized run: finalSimulation
              {reports?.report?.fabId ? ` · Fab ${reports.report.fabId}` : ''}
              {reports?.report?.generatedAt
                ? ` · ${new Date(reports.report.generatedAt).toLocaleString()}`
                : ''}
            </p> */}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            Close
          </button>
        </header>

        <div className="space-y-6 overflow-y-auto px-5 py-4">
          {!reports ? (
            <p className="text-sm text-slate-500">
              No report data yet. Open the Simulator and run a simulation — reports from the API
              response will appear here.
            </p>
          ) : (
            <>
              {(getCompareAfterKpis(reports) || getBeforeKpis(reports)) && (
                <Section
                  title="Improvement"
                  description="Before = SimPy without opt (FIFO). After = SimPy with opt (greedy). Same engine — shows assignment gains for cycle, throughput, and OHT util."
                >
                  <FocusImprovementPanel reports={reports} />
                </Section>
              )}

              {/* Heading intentionally omitted: the KPI cards speak for themselves. */}
              <Section>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  <KpiCard
                    label="Cycle time"
                    value={afterCycle != null ? `${fmt(afterCycle, 2)} hr` : '—'}
                    hint="Lot cycle = makespan ÷ 3600. Lower is better."
                    highlight
                    help={METRIC_HELP.cycleTime}
                  />
                  {SHOW_THROUGHPUT_CARDS && (
                    <KpiCard
                      label="Throughput"
                      value={afterThr != null ? `${fmt(afterThr, 1)} lots/day` : '—'}
                      hint="Completed requests ÷ makespan × 24h. Higher is better."
                      highlight
                      help={METRIC_HELP.throughput}
                    />
                  )}
                  <KpiCard
                    label="OHT utilization"
                    value={util(afterOhtUtil)}
                    hint="Busy time ÷ (makespan × vehicles). Higher is better."
                    highlight
                    help={METRIC_HELP.ohtUtilization}
                  />
                  <KpiCard
                    label="Makespan (s)"
                    value={fmt(simKpis?.makespanS)}
                    hint="Sim clock when the last job finished."
                    help={METRIC_HELP.makespan}
                  />
                  <KpiCard
                    label="Completed"
                    value={fmt(simKpis?.completedRequests, 0)}
                    hint="Transport requests finished in the horizon."
                    help={METRIC_HELP.completed}
                  />
                  <KpiCard
                    label="Avg wait (s)"
                    value={fmt(simKpis?.avgWaitS)}
                    hint="Mean wait before pickup / clearance."
                    help={METRIC_HELP.avgWait}
                  />
                  <KpiCard
                    label="Avg delivery (s)"
                    value={fmt(simKpis?.avgDeliveryS)}
                    hint="Mean loaded travel time per request."
                    help={METRIC_HELP.avgDelivery}
                  />
                  <KpiCard
                    label="Vehicles"
                    value={fmt(simKpis?.vehicleCount, 0)}
                    hint="OHT fleet size used in this run."
                    help={METRIC_HELP.vehicles}
                  />
                </div>
              </Section>
              {/* 
              <Section
                title="Before vs after detail"
                description="Same scorecard as the Before vs After Summary (throughput, pickup, travel, cycle, util, empty travel, congestion, tool starvation)."
              >
                <CompareTable reports={reports} />
              </Section> */}

              {/* <Section title="Executive summary">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  <KpiCard label="Assigned TRs" value={fmt(exec?.assignedRequests, 0)} />
                  <KpiCard
                    label="Cycle improvement"
                    value={improveDeltaLabel(
                      typeof exec?.improvementPercent === 'number'
                        ? exec.improvementPercent
                        : null
                    ).text}
                    highlight
                  />
                  <KpiCard label="Cost saved (s)" value={fmt(exec?.costSavedS)} />
                  <KpiCard label="Total cost (s)" value={fmt(exec?.totalCostS)} />
                  <KpiCard label="OHTs" value={fmt(exec?.vehicleCount, 0)} />
                  <KpiCard label="Reservations" value={fmt(exec?.segmentReservationCount, 0)} />
                  <KpiCard label="Conflicts" value={fmt(exec?.conflictEventCount, 0)} />
                </div>
              </Section> */}

              <Section title="OHT workload">
                {byOht.length === 0 ? (
                  <p className="text-sm text-slate-500">No OHT workload rows.</p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2 font-semibold">OHT</th>
                          <th className="px-3 py-2 font-semibold">Requests</th>
                          <th className="px-3 py-2 font-semibold">Cost (s)</th>
                          <th className="px-3 py-2 font-semibold">Path (m)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {byOht.map((row) => (
                          <tr key={row.ohtId} className="text-slate-700">
                            <td className="px-3 py-2 font-medium">{row.ohtId}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(row.requestCount, 0)}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(row.totalCostS)}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(row.pathLengthM)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* <Section title="Assignments">
                {assignments.length === 0 ? (
                  <p className="text-sm text-slate-500">No assignments in report.</p>
                ) : (
                  <div className="max-h-56 overflow-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-left text-xs">
                      <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2 font-semibold">TR</th>
                          <th className="px-3 py-2 font-semibold">OHT</th>
                          <th className="px-3 py-2 font-semibold">Route</th>
                          <th className="px-3 py-2 font-semibold">Start (s)</th>
                          <th className="px-3 py-2 font-semibold">Cost (s)</th>
                          <th className="px-3 py-2 font-semibold">Path (m)</th>
                          <th className="px-3 py-2 font-semibold">CA</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {assignments.map((a) => (
                          <tr key={a.requestId} className="text-slate-700">
                            <td className="px-3 py-2 font-medium">{a.requestId}</td>
                            <td className="px-3 py-2">{a.assignedOht}</td>
                            <td className="px-3 py-2">{a.selectedRouteId ?? '—'}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(a.plannedStartSec)}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(a.totalCostS)}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(a.pathLengthM)}</td>
                            <td className="px-3 py-2">
                              {a.collisionResolution ?? a.collisionAvoidance?.resolution ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              <Section title="Segment reservations">
                {reservations.length === 0 ? (
                  <p className="text-sm text-slate-500">No reservations in report.</p>
                ) : (
                  <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-left text-xs">
                      <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-3 py-2 font-semibold">Segment</th>
                          <th className="px-3 py-2 font-semibold">TR</th>
                          <th className="px-3 py-2 font-semibold">OHT</th>
                          <th className="px-3 py-2 font-semibold">Start</th>
                          <th className="px-3 py-2 font-semibold">End</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {reservations.slice(0, 40).map((r, i) => (
                          <tr key={`${r.segmentId}-${r.requestId}-${i}`} className="text-slate-700">
                            <td className="px-3 py-2 font-medium">{r.segmentId}</td>
                            <td className="px-3 py-2">{r.requestId}</td>
                            <td className="px-3 py-2">{r.ohtId}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(r.startS)}</td>
                            <td className="px-3 py-2 tabular-nums">{fmt(r.endS)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {reservations.length > 40 ? (
                      <p className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-400">
                        Showing 40 of {reservations.length} reservations
                      </p>
                    ) : null}
                  </div>
                )}
              </Section> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmhsReportsModal;
